/**
 * Finds the assistant `action` blocks in a streamed chat response, so the chat hooks can apply them as events instead
 * of applying them while rendering markdown.
 *
 * The contract is deliberately small: an action is a closed, top-level `ui_component` fence whose JSON has
 * `"kind": "action"`. That is exactly what the server writes, through SeeqContext.stream_ui_component:
 *
 *     ```ui_component
 *     {"kind": "action", "type": "refresh_action_board", "data": {"tileId": "t1"}}
 *     ```
 *
 * Tools emit actions; agents never author them (SPEC/genai/TOOL_SPEC.md). An action written anywhere else, in a list
 * item or block quote, in an inline code span or in a `json` fence, is inert: the renderer shows nothing for it and
 * this module emits nothing. The genai unit test test_ui_component_stream_contract.py pins the server's format against
 * the fixture the unit tests here replay, so a change on either side fails a test.
 *
 * The extractor reads the stream line by line. A fence line starts at column 0 with three or more backticks or tildes;
 * it is top level because it is not indented (a fence a list item holds is indented at least two spaces, and a block
 * quote's lines start with `>`) and because fences cannot nest. An opening fence takes the lines that follow until its
 * closing fence: the same character, at least as many of them, nothing else on the line. Only a `ui_component` fence
 * is parsed; every other fence (prose code, a thinking-section marker, a longer fence quoting an action as an example)
 * is skipped to its close, so nothing inside it can count. A fence that never closes emits nothing.
 */

export type AssistantActionBlock = {
  /** Position among the blocks emitted for the response (0, 1, 2, ...), for ordering only. */
  ordinal: number;
  /** How many earlier blocks in the response had the same raw JSON (0-based), in document order. */
  occurrence: number;
  type: string;
  data: Record<string, unknown>;
  /** The fence body verbatim, which is what the receipt ledger keys on. */
  raw: string;
};

export type AssistantActionExtractor = {
  /** Adds streamed text and returns the action fences it closed. */
  push(chunk: string): AssistantActionBlock[];
  /** Ends the stream: reads a last line that has no line break and returns the action fence it closes, if any. */
  finish(): AssistantActionBlock[];
};

const ACTION_KIND = 'action';
const ACTION_FENCE_LANGUAGE = 'ui_component';
const BACKTICK = '`';

// A fence line at column 0: three or more backticks or tildes, then the info string.
const FENCE_LINE = /^(`{3,}|~{3,})(.*?)\r?$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Parses one JSON payload and returns it as an action, or null when it is not an action block. */
export const parseAssistantActionJson = (raw: string): Pick<AssistantActionBlock, 'type' | 'data'> | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.kind !== ACTION_KIND || typeof parsed.type !== 'string') {
    return null;
  }
  return { type: parsed.type, data: isRecord(parsed.data) ? parsed.data : {} };
};

type OpenFence = { marker: string; language: string; body: string[] };

/** The fence a line opens, or null. A backtick fence's info string may not itself hold a backtick. */
const openFence = (line: string): OpenFence | null => {
  const match = line.match(FENCE_LINE);
  if (!match) return null;
  const [, marker, info] = match;
  if (marker[0] === BACKTICK && info.includes(BACKTICK)) return null;
  return { marker, language: info.trim().split(/\s+/)[0], body: [] };
};

const closesFence = (line: string, fence: OpenFence): boolean => {
  const match = line.match(FENCE_LINE);
  if (!match) return false;
  const [, marker, rest] = match;
  return marker[0] === fence.marker[0] && marker.length >= fence.marker.length && rest.trim() === '';
};

export const createAssistantActionExtractor = (): AssistantActionExtractor => {
  /** Text after the last line break: a line still arriving. */
  let partial = '';
  let fence: OpenFence | null = null;
  let emittedCount = 0;
  const occurrences = new Map<string, number>();
  let finished = false;

  const readLine = (line: string, out: AssistantActionBlock[]) => {
    if (fence === null) {
      fence = openFence(line);
      return;
    }
    if (!closesFence(line, fence)) {
      if (fence.language === ACTION_FENCE_LANGUAGE) fence.body.push(line);
      return;
    }
    const { language, body } = fence;
    fence = null;
    if (language !== ACTION_FENCE_LANGUAGE) return;
    const raw = body.join('\n');
    const action = parseAssistantActionJson(raw);
    if (!action) return;
    const occurrence = occurrences.get(raw) ?? 0;
    occurrences.set(raw, occurrence + 1);
    out.push({ ordinal: emittedCount++, occurrence, ...action, raw });
  };

  return {
    push(chunk: string) {
      if (finished) return [];
      const out: AssistantActionBlock[] = [];
      const lines = (partial + chunk).split('\n');
      partial = lines.pop()!;
      for (const line of lines) readLine(line, out);
      return out;
    },
    finish() {
      if (finished) return [];
      finished = true;
      const out: AssistantActionBlock[] = [];
      if (partial !== '') readLine(partial, out);
      partial = '';
      return out;
    },
  };
};
